import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isNil, sortBy } from 'lodash';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { ActivityService } from 'src/api/activity/activity/activity.service';
import { SectionService } from 'src/api/activity/section/section.service';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';
import { IActivity, ISection } from 'typings/doenkids/doenkids';
import { IAiAdjustmentResponse } from 'typings/api-activity';
import { IAiSectionCreate, planSectionWrites } from './ai-adjustment.model';

/**
 * Works out the sections to create on a copy.
 *
 * Different from `planSectionWrites` because a copy starts empty: everything
 * the proposal keeps has to be created, not updated, and the tombstones are
 * simply left out.
 *
 * @param sections The source activity's current sections
 * @param proposal The accepted proposal
 * @returns The sections to create, and the type names that had to be skipped
 */
export function planSectionCopies(
  sections: ISection[],
  proposal: IAiAdjustmentResponse,
): { creates: IAiSectionCreate[]; skipped: string[] } {
  const sectionById = new Map<number, ISection>();
  for (const section of sections ?? []) {
    if (!isNil(section?.id)) {
      sectionById.set(section.id, section);
    }
  }

  const creates: IAiSectionCreate[] = [];
  const skipped: string[] = [];

  const proposedSections = sortBy(proposal.sections ?? [], (section) => section._index);

  proposedSections.forEach((proposed, position) => {
    if (proposed._action === 'deleted') {
      return;
    }

    const current = proposed._action === 'created' ? undefined : sectionById.get(proposed.id);

    // The proposal reports an unchanged section's data verbatim, but the stored
    // section is the authority for it: that is the row the user has been
    // looking at.
    //
    const data = proposed._action === 'none' && !isNil(current)
      ? ((current.data ?? {}) as Record<string, unknown>)
      : (proposed.data ?? {});

    const typeId = proposed.type_id ?? current?.type_id ?? null;

    if (isNil(typeId)) {
      skipped.push(proposed.type);
      return;
    }

    creates.push({
      type_id: typeId,
      name: isNil(current) ? proposed.type : current.name,
      data,
      // `order` is null only for the tombstones already filtered out, so the
      // fallback just keeps the proposed sequence intact.
      //
      order: proposed.order ?? position + 1,
      // A multi-column section would otherwise land single-column, which the
      // manual copy path does not do. A newly proposed section has no source
      // to copy from and keeps the API default.
      //
      columns: current?.columns,
    });
  });

  return { creates, skipped };
}

/** The status id of a concept activity. A copy always lands as a concept. */
const CONCEPT_STATUS_ID = 1;

/** What the accept flows need in order to write a proposal down. */
export interface IApplyProposalParams {
  /** The activity as currently stored */
  activity: IActivity;
  /** The activity's current sections */
  sections: ISection[];
  /** The accepted proposal */
  proposal: IAiAdjustmentResponse;
}

/** Everything `applyAsCopy` needs on top of the proposal. */
export interface IApplyAsCopyParams extends IApplyProposalParams {
  /** The organization unit to create the copy for */
  organizationUnitId: number;
  /** The country code of that organization unit */
  countryCode: string;
  /** The content language for the copy */
  language: string;
}

/**
 * Writes an accepted AI proposal down, either onto the adjusted activity or
 * onto a fresh copy of it.
 *
 * The one rule that shapes everything here: the adjustment identifier is single
 * use. Exactly one create-or-update may carry it, which closes the ledger row
 * and sets `ai_generated`; every other write in the flow goes out without it. A
 * second attempt is rejected with "Unknown or already used AI adjustment", so
 * there is no retrying a half-applied proposal.
 */
@Injectable({
  providedIn: 'root',
})
export class AiAdjustmentApplyService {
  constructor(
    private activityService: ActivityService,
    private sectionService: SectionService,
    private $i18nToastProvider: I18nToastProvider,
  ) {}

  /**
   * Applies the proposal to the activity it was generated from.
   *
   * The activity update goes first and on its own. If it fails, nothing else
   * has been written and the proposal is still open, so the user can try again.
   * Section writes follow in parallel; a partial failure there is reported
   * rather than rolled back, matching how the editor's own multi-section save
   * behaves.
   *
   * @returns The updated activity
   */
  public async applyToExisting({ activity, sections, proposal }: IApplyProposalParams): Promise<IActivity> {
    const updatedActivity = await this.updateActivity(activity, proposal);

    const plan = planSectionWrites(sections, proposal);

    this.reportSkippedSections(plan.skipped);

    // Deliberately after the activity write, and deliberately in parallel:
    // section order is a field on each row, not a sequence, so nothing here
    // depends on anything else here.
    //
    await Promise.all([
      ...plan.updates.map((update) => this.sectionService.update(update as ISection)),
      ...plan.creates.map((create) => this.sectionService.create({
        ...create,
        activity_id: activity.id,
      } as ISection)),
      ...plan.archives.map((sectionId) => this.sectionService.archive(sectionId)),
    ]);

    return updatedActivity;
  }

  /**
   * Creates a new concept activity from the proposal, leaving the adjusted
   * activity untouched.
   *
   * The create carries the adjustment identifier, so the relation copy and the
   * section writes that follow must not.
   *
   * @returns The created activity
   */
  public async applyAsCopy({
    activity,
    sections,
    proposal,
    organizationUnitId,
    countryCode,
    language,
  }: IApplyAsCopyParams): Promise<IActivity> {
    // Built field by field rather than spread from the source: a copy is a new
    // row, and carrying `id`, `status_id` or the timestamps over would either
    // target the original or resurrect its publication state.
    //
    const newActivity: IActivity = {
      // Trimmed to match the comparison: buildComparisonRows normalises
      // whitespace before deciding a row changed, so writing the untrimmed
      // text would store padding the dialog reported as no change at all.
      //
      name: `${proposal.name ?? ''}`.trim(),
      status_id: CONCEPT_STATUS_ID,
      subtitle: isNil(proposal.subtitle) ? undefined : `${proposal.subtitle}`.trim(),
      summary: isNil(proposal.summary) ? undefined : `${proposal.summary}`.trim(),
      duration: activity.duration,
      location: activity.location,
      media_uuid: activity.media_uuid,
      preparation: activity.preparation,
      language: `${language}`.toLowerCase(),
      country_code: `${countryCode}`.toUpperCase(),
      replace_activity_id: activity.id,
    };

    const createdActivity = await this.createActivity(organizationUnitId, newActivity, proposal.ai_adjustment_id);

    const creates = planSectionCopies(sections, proposal);

    this.reportSkippedSections(creates.skipped);

    await Promise.all([
      this.activityService.copyRelations(activity.id, createdActivity.id),
      ...creates.creates.map((create) => this.sectionService.create({
        ...create,
        activity_id: createdActivity.id,
      } as ISection)),
    ]);

    return createdActivity;
  }

  /**
   * The one call in `applyToExisting` that carries the adjustment identifier.
   *
   * Extracted so the ordering guarantee above is a single named step rather
   * than an inline call it would be easy to move.
   */
  private async updateActivity(activity: IActivity, proposal: IAiAdjustmentResponse): Promise<IActivity> {
    // Null rather than undefined: `undefined` is dropped by JSON.stringify, so
    // a proposal that clears the subtitle or summary would be shown as a change
    // and then silently not applied.
    //
    const updated: IActivity = {
      ...activity,
      name: `${proposal.name ?? ''}`.trim(),
      subtitle: isNil(proposal.subtitle) ? proposal.subtitle : `${proposal.subtitle}`.trim(),
      summary: isNil(proposal.summary) ? proposal.summary : `${proposal.summary}`.trim(),
    };

    // `ai_generated` is server set and stripped from any request body, so
    // sending it would be noise at best; dropped explicitly to keep that
    // obvious at the call site.
    //
    delete updated.ai_generated;

    return this.activityService.update(activity.id, updated, proposal.ai_adjustment_id);
  }

  /**
   * The one call in `applyAsCopy` that carries the adjustment identifier.
   *
   * `ActivityService.create` returns a cold observable rather than a promise,
   * so it needs subscribing: awaiting it directly would hand back the
   * observable and never send the request.
   */
  private async createActivity(organizationUnitId: number, activity: IActivity, aiAdjustmentId: number): Promise<IActivity> {
    return (await firstValueFrom(this.activityService.create(organizationUnitId, activity, aiAdjustmentId))) as IActivity;
  }

  /**
   * Tells the user which proposed sections could not be written.
   *
   * Only possible for a created section whose type this platform does not have.
   * Reported rather than thrown: the generation is already billed and the rest
   * of the proposal is worth keeping.
   */
  private reportSkippedSections(skipped: string[]): void {
    if (skipped.length === 0) {
      return;
    }

    this.$i18nToastProvider.error(_('activity.ai_adjustment.apply.sections_skipped'), { count: skipped.length });
  }
}
